import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Pressable,
  Image,
  useWindowDimensions,
} from 'react-native';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor } from '../../utils/metricColors';
import { organizationsApi, goalsApi, initiativesApi, kpisApi } from '../../services/apiService';
import { Organization, StrategicGoalList, InitiativeList, KPIList } from '../../types';
import { COLORS, FONTS, API_BASE_URL } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, Breadcrumb, OrganizationalChart } from '../../components';
import { useNotification } from '../../contexts/NotificationContext';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useRoleNavigation, useResponsive, useAutoTranslate } from '../../hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useTranslation } from 'react-i18next';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
type RouteProps = RouteProp<MainStackParamList, 'OrganizationDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TargetIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChartColumnBigIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 3V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H21" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18 5H16C15.4477 5 15 5.44772 15 6V16C15 16.5523 15.4477 17 16 17H18C18.5523 17 19 16.5523 19 16V6C19 5.44772 18.5523 5 18 5Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 8H8C7.44772 8 7 8.44772 7 9V16C7 16.5523 7.44772 17 8 17H10C10.5523 17 11 16.5523 11 16V9C11 8.44772 10.5523 8 10 8Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const RocketIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4.5 16.4993C3 17.7593 2.5 21.4993 2.5 21.4993C2.5 21.4993 6.24 20.9993 7.5 19.4993C8.21 18.6593 8.2 17.3693 7.41 16.5893C7.02131 16.2184 6.50929 16.004 5.97223 15.9874C5.43516 15.9708 4.91088 16.1531 4.5 16.4993Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15.0002L9 12.0002C9.53214 10.6197 10.2022 9.29631 11 8.05025C12.1652 6.18723 13.7876 4.6533 15.713 3.59434C17.6384 2.53538 19.8027 1.98662 22 2.00025C22 4.72025 21.22 9.50025 16 13.0002C14.7369 13.799 13.3968 14.469 12 15.0002Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11.9995H4C4 11.9995 4.55 8.96953 6 7.99953C7.62 6.91953 11 7.99953 11 7.99953" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V20C12 20 15.03 19.45 16 18C17.08 16.38 16 13 16 13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const GoalIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 13V2L20 6L12 10" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20.5607 10.2216C21.0928 11.8618 21.1426 13.6203 20.7041 15.2879C20.2656 16.9555 19.3573 18.4621 18.0872 19.6283C16.8172 20.7946 15.2388 21.5714 13.5399 21.8665C11.841 22.1615 10.0932 21.9623 8.50424 21.2925C6.91532 20.6228 5.55224 19.5107 4.57717 18.0885C3.6021 16.6664 3.05609 14.9941 3.00409 13.2706C2.9521 11.547 3.39631 9.84486 4.28388 8.36653C5.17145 6.8882 6.46502 5.69595 8.01068 4.93164" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8.00238 9.99609C7.50129 10.6631 7.17506 11.4449 7.05343 12.2703C6.93179 13.0956 7.01862 13.9383 7.30599 14.7215C7.59336 15.5048 8.07214 16.2036 8.69871 16.7545C9.32529 17.3053 10.0797 17.6906 10.8933 17.8753C11.7069 18.0599 12.5538 18.0381 13.3567 17.8117C14.1597 17.5853 14.8933 17.1616 15.4906 16.5792C16.0879 15.9967 16.53 15.2741 16.7766 14.4771C17.0232 13.6801 17.0664 12.8341 16.9024 12.0161" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BadgeCheckIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3.85019 8.61912C3.70423 7.96165 3.72665 7.27796 3.91535 6.63146C4.10405 5.98496 4.45294 5.39657 4.92966 4.92084C5.40638 4.44512 5.9955 4.09747 6.6424 3.91012C7.2893 3.72277 7.97303 3.70179 8.63019 3.84912C8.9919 3.28342 9.4902 2.81788 10.0791 2.49541C10.6681 2.17293 11.3287 2.00391 12.0002 2.00391C12.6716 2.00391 13.3323 2.17293 13.9212 2.49541C14.5102 2.81788 15.0085 3.28342 15.3702 3.84912C16.0284 3.70114 16.7133 3.72203 17.3612 3.90983C18.0091 4.09764 18.599 4.44626 19.076 4.92327C19.5531 5.40029 19.9017 5.99019 20.0895 6.63812C20.2773 7.28605 20.2982 7.97095 20.1502 8.62912C20.7159 8.99083 21.1814 9.48912 21.5039 10.0781C21.8264 10.667 21.9954 11.3277 21.9954 11.9991C21.9954 12.6706 21.8264 13.3312 21.5039 13.9202C21.1814 14.5091 20.7159 15.0074 20.1502 15.3691C20.2975 16.0263 20.2765 16.71 20.0892 17.3569C19.9018 18.0038 19.5542 18.5929 19.0785 19.0697C18.6027 19.5464 18.0144 19.8953 17.3679 20.084C16.7213 20.2727 16.0377 20.2951 15.3802 20.1491C15.019 20.717 14.5203 21.1845 13.9303 21.5084C13.3404 21.8324 12.6782 22.0022 12.0052 22.0022C11.3322 22.0022 10.67 21.8324 10.0801 21.5084C9.49011 21.1845 8.99143 20.717 8.63019 20.1491C7.97303 20.2965 7.2893 20.2755 6.6424 20.0881C5.9955 19.9008 5.40638 19.5531 4.92966 19.0774C4.45294 18.6017 4.10405 18.0133 3.91535 17.3668C3.72665 16.7203 3.70423 16.0366 3.85019 15.3791C3.28015 15.0184 2.81061 14.5193 2.48524 13.9283C2.15988 13.3374 1.98926 12.6737 1.98926 11.9991C1.98926 11.3245 2.15988 10.6609 2.48524 10.0699C2.81061 9.47895 3.28015 8.97988 3.85019 8.61912Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 12L11 14L15 10" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EyeIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.06153 12.3474C1.97819 12.1229 1.97819 11.8759 2.06153 11.6514C2.87323 9.68324 4.25104 8.00042 6.0203 6.81628C7.78955 5.63214 9.87057 5 11.9995 5C14.1285 5 16.2095 5.63214 17.9788 6.81628C19.748 8.00042 21.1258 9.68324 21.9375 11.6514C22.0209 11.8759 22.0209 12.1229 21.9375 12.3474C21.1258 14.3155 19.748 15.9983 17.9788 17.1825C16.2095 18.3666 14.1285 18.9988 11.9995 18.9988C9.87057 18.9988 7.78955 18.3666 6.0203 17.1825C4.25104 15.9983 2.87323 14.3155 2.06153 12.3474Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FlagIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M7 22V2L17 7L7 12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CheckIcon = ({ size = 16, color = tokens.colors.raw.green["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Decision Maker Card Icons
const ContactRoundIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M16 18C16 15.7909 13.3137 14 10 14C6.68629 14 4 15.7909 4 18" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Circle cx="10" cy="8" r="4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 18C20 15.7909 18.2091 14 16 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 4C17.6569 4 19 5.34315 19 7C19 8.65685 17.6569 10 16 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FolderCogIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 20C21.1046 20 22 19.1046 22 18V7C22 5.89543 21.1046 5 20 5H12.4142C12.149 5 11.8946 4.89464 11.7071 4.70711L9.58579 2.58579C9.21071 2.21071 8.70201 2 8.17157 2H4C2.89543 2 2 2.89543 2 4V18C2 19.1046 2.89543 20 4 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Circle cx="14" cy="15" r="2" stroke={color} strokeWidth="1.5"/>
    <Path d="M14 11V13" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <Path d="M14 17V19" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <Path d="M10.5 13L12.2 14" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <Path d="M15.8 16L17.5 17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <Path d="M10.5 17L12.2 16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <Path d="M15.8 14L17.5 13" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </Svg>
);

const HandServicesIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 14H7C5.89543 14 5 14.8954 5 16V18C5 19.1046 5.89543 20 7 20H8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M15 10C17.2091 10 19 11.7909 19 14V22H5L5 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M3 16H5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9.44698 2.44699L12.447 5.44699C13.228 6.22799 13.228 7.49399 12.447 8.27499L11.739 8.98199C10.958 9.76299 9.69298 9.76299 8.91198 8.98199L5.91198 5.98199" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10.5 6.5L11.5 7.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BuildingIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 22V4C6 3.46957 6.21071 2.96086 6.58579 2.58579C6.96086 2.21071 7.46957 2 8 2H16C16.5304 2 17.0391 2.21071 17.4142 2.58579C17.7893 2.96086 18 3.46957 18 4V22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 22H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 12H6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18 9H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 6H10.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 10H10.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 14H10.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 18H10.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 6H14.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 10H14.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Circle cx="20" cy="16" r="2" stroke={color} strokeWidth="1.5"/>
    <Circle cx="20" cy="14" r="4" stroke={color} strokeWidth="1.5" strokeDasharray="0.1 2" strokeLinecap="round"/>
  </Svg>
);

// ============================================================
// Circular Progress Component
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  textColor?: string;
}

const CircularGauge = ({ percentage, size = 120, strokeWidth = 12, color = tokens.colors.raw.green["500"], backgroundColor = tokens.colors.raw.gray["200"], textColor = '#303030' }: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;
  const center = size / 2;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <Circle cx={center} cy={center} r={radius} stroke={backgroundColor} strokeWidth={strokeWidth} fill="none" />
        <Circle cx={center} cy={center} r={radius} stroke={color} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          style={{ fontSize: Math.max(10, Math.round(size / 5)), fontFamily: FONTS.numbers, fontWeight: '500', color: textColor }}
        >%{formatPct(safePercentage)}</Text>
      </View>
    </View>
  );
};

// Donut gauge with percentage text
const DonutGauge = ({ percentage, size = 32, strokeWidth = 4, color = tokens.colors.raw.green["500"], backgroundColor = tokens.colors.raw.gray["200"] }: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;
  const center = size / 2;

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <Circle cx={center} cy={center} r={radius} stroke={backgroundColor} strokeWidth={strokeWidth} fill="none" />
        <Circle cx={center} cy={center} r={radius} stroke={color} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
      </Svg>
    </View>
  );
};

// ============================================================
// Goal Section Component
// ============================================================

interface GoalSectionProps {
  goal: StrategicGoalList;
  kpis: KPIList[];
  onViewDetails: (goalId: number) => void;
  onKPIViewDetails: (kpiId: number) => void;
  canApprove?: boolean;
  onApprove?: (goalId: number) => void;
  onReject?: (goalId: number) => void;
  cardWidth?: number;
}

const GoalSection = ({ goal, kpis, onViewDetails, onKPIViewDetails, canApprove, onApprove, onReject, cardWidth }: GoalSectionProps) => {
  const { translate } = useAutoTranslate();
  const { t } = useTranslation();
  const percentage = parseFloat(Number(goal.commitment_rate ?? 0).toFixed(2));
  const getPercentageColor = () => {
    if (percentage >= 80) return tokens.colors.raw.green["500"];
    if (percentage >= 50) return tokens.colors.raw.orange["500"];
    return tokens.colors.raw.red["500"];
  };

  const isPending = goal.status === 'pending';

  return (
    <View style={styles.goalSection}>
      {/* Goal Header */}
      <View style={styles.goalHeader}>
        {/* Left: Actions */}
        <View style={styles.goalLeftGroup}>
          <TouchableOpacity style={styles.viewDetailsBtn} onPress={() => onViewDetails(goal.id)}>
            <EyeIcon size={16} color={tokens.colors.raw.gray["500"]} />
            <Text style={styles.viewDetailsBtnText}>{t('common.viewDetails')}</Text>
          </TouchableOpacity>

          <View style={styles.percentageGroup}>
            <DonutGauge percentage={percentage} size={32} strokeWidth={4} color={getPercentageColor()} />
            <Text style={styles.percentageText}>%{percentage}</Text>
          </View>
        </View>

        {/* Right: Goal Title */}
        <View style={styles.goalTitleContainer}>
          <Text style={styles.goalTitle}>{translate(goal.title)}</Text>
          <View style={[styles.bullet, { backgroundColor: getPercentageColor() }]} />
        </View>
      </View>

      {/* Approval Buttons - Only for users who can approve (NOT executive) */}
      {canApprove && isPending && (
        <View style={styles.approvalButtons}>
          <Button
            variant="destructive"
            onPress={() => onReject?.(goal.id)}
            leadingIcon={<XIcon size={16} color={tokens.colors.raw.white} />}
          >
            {t('common.reject')}
          </Button>
          <Button
            variant="primary"
            onPress={() => onApprove?.(goal.id)}
            leadingIcon={<CheckIcon size={16} color={tokens.colors.raw.white} />}
          >
            {t('common.approve')}
          </Button>
        </View>
      )}

      {/* KPI Cards - Horizontal Scroll with RTL transform */}
      <View style={styles.rtlWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          contentContainerStyle={styles.horizontalCardsRow}
          style={styles.horizontalScrollContainer}
        >
          {kpis.length > 0 ? kpis.map((kpi) => {
            // Commitment rate (نسبة الالتزام); null = nothing due yet → muted —
            const kpiCommitment = kpi.commitment_rate ?? null;
            const kpiColor = kpiCommitment == null ? tokens.colors.raw.gray["400"] : commitmentColor(kpiCommitment);
            const displayValue = kpi.latest_value && kpi.target_value
              ? `${kpi.target_value}\\${kpi.latest_value}`
              : kpiCommitment == null ? '—' : `% ${kpiCommitment}`;

            return (
              <View key={kpi.id} style={[styles.rtlCard, styles.kpiCard, cardWidth && { width: cardWidth }]}>
                <Text style={styles.kpiCardTitle}>{translate(kpi.name)}</Text>
                <View style={styles.kpiValueRow}>
                  <View style={styles.kpiValueBadge}>
                    <Text style={styles.kpiValue}>{displayValue}</Text>
                  </View>
                  <View style={styles.kpiProgressContainer}>
                    <View style={styles.kpiProgressTrack}>
                      <View style={[styles.kpiProgressFill, { width: `${kpiCommitment ?? 0}%`, backgroundColor: kpiColor }]} />
                    </View>
                  </View>
                </View>
                <TouchableOpacity style={styles.kpiDetailsLink} onPress={() => onKPIViewDetails(kpi.id)}>
                  <ChevronLeftIcon size={16} color={tokens.colors.semantic.general.foreground} />
                  <Text style={styles.kpiDetailsText}>{t('common.viewDetails')}</Text>
                </TouchableOpacity>
              </View>
            );
          }) : (
            <View style={[styles.emptyKpis, styles.rtlCard]}>
              <Text style={styles.emptyKpisText}>{t('dashboard.noKPIs')}</Text>
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

// ============================================================
// Initiative Section Component
// ============================================================

interface InitiativeSectionProps {
  initiative: InitiativeList;
  kpis: KPIList[];
  onViewDetails: (id: number) => void;
  onKPIViewDetails: (kpiId: number) => void;
  canApprove?: boolean;
  onApprove?: (id: number) => void;
  onReject?: (id: number) => void;
  cardWidth?: number;
}

const InitiativeSection = ({ initiative, kpis, onViewDetails, onKPIViewDetails, canApprove, onApprove, onReject, cardWidth }: InitiativeSectionProps) => {
  const { translate } = useAutoTranslate();
  const { t } = useTranslation();
  const percentage = parseFloat(Number(initiative.commitment_rate ?? 0).toFixed(2));
  const getPercentageColor = () => {
    if (percentage >= 80) return tokens.colors.raw.green["500"];
    if (percentage >= 50) return tokens.colors.raw.orange["500"];
    return tokens.colors.raw.red["500"];
  };

  const isPending = initiative.approval_status === 'pending';

  return (
    <View style={styles.goalSection}>
      {/* Initiative Header */}
      <View style={styles.goalHeader}>
        <View style={styles.goalLeftGroup}>
          <TouchableOpacity style={styles.viewDetailsBtn} onPress={() => onViewDetails(initiative.id)}>
            <EyeIcon size={16} color={tokens.colors.raw.gray["500"]} />
            <Text style={styles.viewDetailsBtnText}>{t('common.viewDetails')}</Text>
          </TouchableOpacity>

          <View style={styles.percentageGroup}>
            <DonutGauge percentage={percentage} size={32} strokeWidth={4} color={getPercentageColor()} />
            <Text style={styles.percentageText}>%{percentage}</Text>
          </View>
        </View>

        <View style={styles.goalTitleContainer}>
          <Text style={styles.goalTitle}>{translate(initiative.title)}</Text>
          <View style={[styles.bullet, { backgroundColor: getPercentageColor() }]} />
        </View>
      </View>

      {/* Approval Buttons - Only for users who can approve (NOT executive) */}
      {canApprove && isPending && (
        <View style={styles.approvalButtons}>
          <Button
            variant="destructive"
            onPress={() => onReject?.(initiative.id)}
            leadingIcon={<XIcon size={16} color={tokens.colors.raw.white} />}
          >
            {t('common.reject')}
          </Button>
          <Button
            variant="primary"
            onPress={() => onApprove?.(initiative.id)}
            leadingIcon={<CheckIcon size={16} color={tokens.colors.raw.white} />}
          >
            {t('common.approve')}
          </Button>
        </View>
      )}

      {/* KPI Cards - Horizontal Scroll with RTL transform */}
      <View style={styles.rtlWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          contentContainerStyle={styles.horizontalCardsRow}
          style={styles.horizontalScrollContainer}
        >
          {kpis.length > 0 ? kpis.map((kpi) => {
            // Commitment rate (نسبة الالتزام); null = nothing due yet → muted —
            const kpiCommitment = kpi.commitment_rate ?? null;
            const kpiColor = kpiCommitment == null ? tokens.colors.raw.gray["400"] : commitmentColor(kpiCommitment);

            return (
              <View key={kpi.id} style={[styles.rtlCard, styles.kpiCard, cardWidth && { width: cardWidth }]}>
                <Text style={styles.kpiCardTitle}>{translate(kpi.name)}</Text>
                <View style={styles.kpiValueRow}>
                  <View style={styles.kpiValueBadge}>
                    <Text style={styles.kpiValue}>{kpiCommitment == null ? '—' : `% ${kpiCommitment}`}</Text>
                  </View>
                  <View style={styles.kpiProgressContainer}>
                    <View style={styles.kpiProgressTrack}>
                      <View style={[styles.kpiProgressFill, { width: `${kpiCommitment ?? 0}%`, backgroundColor: kpiColor }]} />
                    </View>
                  </View>
                </View>
                <TouchableOpacity style={styles.kpiDetailsLink} onPress={() => onKPIViewDetails(kpi.id)}>
                  <ChevronLeftIcon size={16} color={tokens.colors.semantic.general.foreground} />
                  <Text style={styles.kpiDetailsText}>{t('common.viewDetails')}</Text>
                </TouchableOpacity>
              </View>
            );
          }) : (
            <View style={[styles.emptyKpis, styles.rtlCard]}>
              <Text style={styles.emptyKpisText}>{t('dashboard.noKPIs')}</Text>
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};


// ============================================================
// Main Component
// ============================================================

export default function OrganizationDetailScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProp>();
  const { showSuccess, showError } = useNotification();
  const { width } = useWindowDimensions();
  const { horizontalCardWidth } = useResponsive();
  const { canApprove } = useRoleNavigation();
  const { id: organizationId } = route.params;
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { t } = useTranslation();
  const { selectedYear } = useYear();

  // Theme colors for dark mode support
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    textSecondary: colors.textSecondary,
    backgroundAlt: colors.backgroundAlt,
    border: colors.border,
    iconDefault: colors.iconDefault,
    goalSectionBg: isDark ? '#1A1A1A' : tokens.colors.raw.slate["100"],
    progressTrack: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
    kpiValueBadge: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
    orgStatPill: isDark ? tokens.colors.raw.gray["800"] : tokens.colors.raw.gray["50"],
    circleTrack: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"],
    gaugeText: colors.textPrimary,
  };

  const isSmallScreen = width < 768;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [goalKpis, setGoalKpis] = useState<Record<number, KPIList[]>>({});
  const [initiatives, setInitiatives] = useState<InitiativeList[]>([]);
  const [initiativeKpis, setInitiativeKpis] = useState<Record<number, KPIList[]>>({});
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);

      // Fetch organization and dashboard
      const yearParams = selectedYear ? { year: selectedYear } : undefined;
      const [orgData, dashboard] = await Promise.all([
        organizationsApi.get(organizationId, yearParams),
        organizationsApi.getDashboard(organizationId, yearParams).catch(() => null),
      ]);
      setOrganization(orgData);
      setDashboardData(dashboard);

      // Fetch goals for this organization
      try {
        const goalsResponse = await goalsApi.list({ department_id: organizationId, page_size: 100 });
        const goalsList = goalsResponse.results || [];
        setGoals(goalsList);

        // Fetch KPIs for each goal
        const kpisMap: Record<number, KPIList[]> = {};
        await Promise.all(
          goalsList.map(async (goal) => {
            try {
              const kpis = await goalsApi.getKPIs(goal.id);
              kpisMap[goal.id] = kpis;
            } catch {
              kpisMap[goal.id] = [];
            }
          })
        );
        setGoalKpis(kpisMap);
      } catch (err) {
        console.log('Could not fetch goals');
      }

      // Fetch initiatives for this organization
      try {
        const initiativesResponse = await initiativesApi.list({ organization: organizationId, page_size: 100 });
        const initiativesList = initiativesResponse.results || [];
        setInitiatives(initiativesList);

        // Fetch KPIs for each initiative
        const initKpisMap: Record<number, KPIList[]> = {};
        await Promise.all(
          initiativesList.map(async (initiative) => {
            try {
              const kpisResponse = await kpisApi.list({ initiative: initiative.id, page_size: 100 });
              initKpisMap[initiative.id] = kpisResponse.results || [];
            } catch {
              initKpisMap[initiative.id] = [];
            }
          })
        );
        setInitiativeKpis(initKpisMap);
      } catch (err) {
        console.log('Could not fetch initiatives');
      }

    } catch (err: any) {
      setError(err.message || t('organizations.loadDataFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [organizationId, selectedYear]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Navigation handlers
  const handleGoalViewDetails = (goalId: number) => {
    navigation.navigate('GoalDetail', { id: goalId });
  };

  const handleKPIViewDetails = (kpiId: number) => {
    navigation.navigate('KPIDetail', { id: kpiId });
  };

  const handleInitiativeViewDetails = (initiativeId: number) => {
    navigation.navigate('InitiativeDetail', { id: initiativeId });
  };

  // Approval handlers
  const handleApproveGoal = async (goalId: number) => {
    try {
      await goalsApi.approve(goalId);
      showSuccess(t('goals.approveSuccess'));
      fetchData();
    } catch (err: any) {
      showError(err.message || t('goals.approveFailed'));
    }
  };

  const handleRejectGoal = async (goalId: number) => {
    try {
      await goalsApi.reject(goalId);
      showSuccess(t('goals.rejectSuccess'));
      fetchData();
    } catch (err: any) {
      showError(err.message || t('goals.rejectFailed'));
    }
  };

  const handleApproveInitiative = async (initiativeId: number) => {
    try {
      await initiativesApi.approve(initiativeId);
      showSuccess(t('initiatives.approveSuccess'));
      fetchData();
    } catch (err: any) {
      showError(err.message || t('initiatives.approveFailed'));
    }
  };

  const handleRejectInitiative = async (initiativeId: number) => {
    try {
      await initiativesApi.reject(initiativeId);
      showSuccess(t('initiatives.rejectSuccess'));
      fetchData();
    } catch (err: any) {
      showError(err.message || t('initiatives.rejectFailed'));
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !organization) {
    return (
      <PageContainer>
        <Navbar />
        <EmptyState
          icon="alert-circle-outline"
          title={t('organizations.errorOccurred')}
          message={error || t('organizations.notFound')}
          actionLabel={t('common.refresh')}
          onAction={fetchData}
        />
      </PageContainer>
    );
  }

  const stats = dashboardData?.statistics;

  // Calculate overall percentages
  const overallGoalsPercentage = parseFloat((Number(organization?.stage_actual_progress ?? 0) * 100).toFixed(2));

  // Commitment rate (نسبة الالتزام) — weighted Σactual/Σrequired over initiative KPIs, from the retrieve payload
  const overallInitiativesPercentage = parseFloat(Number(organization?.initiatives_commitment ?? 0).toFixed(2));

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('nav.home') },
            { label: t('nav.organizations'), route: 'Organizations' },
            { label: translateOrg(organization.name) },
          ]}
        />

        {/* Page Title */}
        <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{translateOrg(organization.name)}</Text>

        {/* Main Content Row */}
        <View style={[styles.mainContent, isSmallScreen && styles.mainContentSmall]}>
          {/* Left Column: KPI Gauges */}
          <View style={[styles.leftColumn, isSmallScreen && styles.leftColumnSmall]}>
            {/* Vision Achievement Commitment */}
            <TouchableOpacity
              style={[styles.kpiGaugeCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => navigation.navigate('GoalsStack' as any, { screen: 'Goals' })}
              activeOpacity={0.7}
            >
              <View style={styles.kpiGaugeCardBody}>
                <CircularGauge
                  percentage={organization.commitment_index ?? 0}
                  size={120}
                  strokeWidth={12}
                  color={(organization.commitment_index ?? 0) >= 80 ? tokens.colors.raw.green["500"] : (organization.commitment_index ?? 0) >= 50 ? tokens.colors.raw.orange["500"] : tokens.colors.raw.red["500"]}
                  backgroundColor={themeColors.circleTrack}
                  textColor={themeColors.gaugeText}
                />
                <View style={styles.kpiGaugeTextSection}>
                  <Text style={[styles.kpiGaugeTitle, { color: themeColors.textPrimary }]}>{t('dashboard.commitmentIndex')}</Text>
                  {/* Legend Items */}
                  <View style={styles.legendItems}>
                    <View style={styles.legendRow}>
                      <View style={[styles.legendDotIndicator, { backgroundColor: tokens.colors.raw.green["500"] }]} />
                      <Text style={[styles.legendRowLabel, { color: themeColors.textMuted }]}>{t('dashboard.excellent')} (≥80%)</Text>
                    </View>
                    <View style={styles.legendRow}>
                      <View style={[styles.legendDotIndicator, { backgroundColor: tokens.colors.raw.orange["500"] }]} />
                      <Text style={[styles.legendRowLabel, { color: themeColors.textMuted }]}>{t('dashboard.acceptable')} (50-79%)</Text>
                    </View>
                    <View style={styles.legendRow}>
                      <View style={[styles.legendDotIndicator, { backgroundColor: tokens.colors.raw.red["500"] }]} />
                      <Text style={[styles.legendRowLabel, { color: themeColors.textMuted }]}>{t('dashboard.poor')} (&lt;50%)</Text>
                    </View>
                  </View>
                </View>
              </View>
            </TouchableOpacity>

            {/* Vision Achievement */}
            <TouchableOpacity
              style={[styles.kpiGaugeCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => navigation.navigate('GoalsStack' as any, { screen: 'Goals' })}
              activeOpacity={0.7}
            >
              <View style={styles.kpiGaugeCardBody}>
                <CircularGauge
                  percentage={parseFloat(((organization.stage_actual_progress ?? 0) * 100).toFixed(2))}
                  size={120}
                  strokeWidth={12}
                  color={parseFloat(((organization.stage_actual_progress ?? 0) * 100).toFixed(2)) >= 80 ? tokens.colors.raw.green["500"] : parseFloat(((organization.stage_actual_progress ?? 0) * 100).toFixed(2)) >= 50 ? tokens.colors.raw.orange["500"] : tokens.colors.raw.red["500"]}
                  backgroundColor={themeColors.circleTrack}
                  textColor={themeColors.gaugeText}
                />
                <View style={styles.kpiGaugeTextSection}>
                  <Text style={[styles.kpiGaugeTitle, { color: themeColors.textPrimary }]}>{t('dashboard.visionAchievementIndex')}</Text>
                  <View style={styles.legendItems}>
                    <View style={styles.legendRow}>
                      <Text style={[styles.legendRowValue, { color: themeColors.textPrimary }]}>% {formatPct((organization.stage_required_progress ?? 0) * 100)}</Text>
                      <View style={styles.legendRowContent}>
                        <Text style={[styles.legendRowLabel, { color: themeColors.textMuted }]}>{t('dashboard.targetRequiredPercentage')}</Text>
                        <TargetIcon size={20} color={themeColors.textPrimary} />
                      </View>
                    </View>
                    <View style={styles.legendRow}>
                      <Text style={[styles.legendRowValue, { color: themeColors.textPrimary }]}>% {formatPct(Number(organization.stage_actual_progress ?? 0) * 100)}</Text>
                      <View style={styles.legendRowContent}>
                        <Text style={[styles.legendRowLabel, { color: themeColors.textMuted }]}>{t('dashboard.actualAchieved')}</Text>
                        <FlagIcon size={20} color={themeColors.textPrimary} />
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          </View>

          {/* Right Column: Organization Info */}
          <View style={[styles.rightColumn, isSmallScreen && styles.rightColumnSmall]}>
            <View style={[styles.orgCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <Text style={[styles.orgName, { color: themeColors.textPrimary }]}>{translateOrg(organization.name)}</Text>
              <View style={styles.orgContent}>
                <Text style={[styles.orgSectionText, { color: themeColors.textPrimary }]}>
                  <Text style={styles.orgSectionTitle}>{t('organizations.ourVisionLabel')}{'\n'}</Text>
                  {translate(organization.vision) || t('dashboard.visionNotSet')}
                </Text>
                <Text style={[styles.orgSectionText, { color: themeColors.textPrimary }]}>
                  <Text style={styles.orgSectionTitle}>{t('organizations.ourMissionLabel')}{'\n'}</Text>
                  {translate(organization.mission) || t('dashboard.missionNotSet')}
                </Text>
                <Text style={[styles.orgSectionText, { color: themeColors.textPrimary }]}>
                  <Text style={styles.orgSectionTitle}>{t('organizations.aboutOrganizationLabel')}{'\n'}</Text>
                  {translate(organization.description) || t('dashboard.aboutNotSet')}
                </Text>
                <Text style={[styles.orgSectionText, { color: themeColors.textPrimary }]}>
                  <Text style={styles.orgSectionTitle}>{t('organizations.ourValuesLabel')}{'\n'}</Text>
                  {translate(organization.values) || t('dashboard.valuesNotSet')}
                </Text>
              </View>
              <View style={[styles.orgStatsRow, { borderTopColor: themeColors.border }]}>
                <View style={[styles.orgStatPill, { backgroundColor: themeColors.orgStatPill }]}>
                  <Text style={[styles.orgStatPillValue, { color: themeColors.textPrimary }]}>{organization.founding_year || '-'}</Text>
                  <Text style={[styles.orgStatPillLabel, { color: themeColors.textMuted }]}>{t('organizations.foundingYear')}</Text>
                </View>
                <View style={[styles.orgStatPill, { backgroundColor: themeColors.orgStatPill }]}>
                  <Text style={[styles.orgStatPillValue, { color: themeColors.textPrimary }]}>{organization.employees_count ?? 0}</Text>
                  <Text style={[styles.orgStatPillLabel, { color: themeColors.textMuted }]}>{t('organizations.employees')}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Stats Cards */}
        <View style={[styles.statsRow, isSmallScreen && styles.statsRowSmall]}>
          <Pressable
            style={({ hovered }: any) => [
              styles.statCard,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              hovered && styles.statCardHovered,
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, { color: themeColors.textMuted }]}>{t('dashboard.strategicGoals')}</Text>
              <GoalIcon size={40} color={themeColors.textPrimary} />
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{stats?.total_goals ?? goals.length}</Text>
          </Pressable>
          <Pressable
            style={({ hovered }: any) => [
              styles.statCard,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              hovered && styles.statCardHovered,
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, { color: themeColors.textMuted }]}>{t('dashboard.strategicKPIs')}</Text>
              <ChartColumnBigIcon size={40} color={themeColors.textPrimary} />
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{stats?.total_kpis ?? 0}</Text>
          </Pressable>
          <Pressable
            style={({ hovered }: any) => [
              styles.statCard,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              hovered && styles.statCardHovered,
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, { color: themeColors.textMuted }]}>{t('dashboard.initiatives')}</Text>
              <RocketIcon size={40} color={themeColors.textPrimary} />
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{stats?.total_initiatives ?? initiatives.length}</Text>
          </Pressable>
          <Pressable
            style={({ hovered }: any) => [
              styles.statCard,
              { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder },
              hovered && styles.statCardHovered,
            ]}
          >
            <View style={styles.statCardHeader}>
              <Text style={[styles.statCardTitle, { color: themeColors.textMuted }]}>{t('dashboard.completedInitiatives')}</Text>
              <BadgeCheckIcon size={40} color={themeColors.textPrimary} />
            </View>
            <Text style={[styles.statCardValue, { color: themeColors.textPrimary }]}>{stats?.completed_initiatives ?? 0}</Text>
          </Pressable>
        </View>



        {/* Strategic Goals Section */}
        <View style={[styles.sectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionPercentageContainer}>
              <CircularGauge percentage={overallGoalsPercentage} size={48} strokeWidth={5} color={tokens.colors.raw.green["500"]} backgroundColor={themeColors.circleTrack} textColor={themeColors.gaugeText} />
            </View>
            <View style={styles.sectionTitleRow}>
              <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('organizations.strategicGoalsKPIs')}</Text>
              <TargetIcon size={40} color={themeColors.textPrimary} />
            </View>
          </View>

          {goals.length > 0 ? goals.slice(0, 5).map((goal) => (
            <GoalSection
              key={goal.id}
              goal={goal}
              kpis={goalKpis[goal.id] || []}
              onViewDetails={handleGoalViewDetails}
              onKPIViewDetails={handleKPIViewDetails}
              canApprove={canApprove}
              cardWidth={horizontalCardWidth}
              onApprove={handleApproveGoal}
              onReject={handleRejectGoal}
            />
          )) : (
            <View style={styles.emptySection}>
              <Text style={[styles.emptySectionText, { color: themeColors.textMuted }]}>{t('organizations.noStrategicGoals')}</Text>
            </View>
          )}
        </View>

        {/* Initiatives Section */}
        <View style={[styles.sectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionPercentageContainer}>
              <CircularGauge percentage={overallInitiativesPercentage} size={48} strokeWidth={5} color={tokens.colors.raw.orange["500"]} backgroundColor={themeColors.circleTrack} textColor={themeColors.gaugeText} />
            </View>
            <View style={styles.sectionTitleRow}>
              <Text style={[styles.sectionTitle, { color: themeColors.textPrimary }]}>{t('organizations.initiativeKPIs')}</Text>
              <RocketIcon size={40} color={themeColors.textPrimary} />
            </View>
          </View>

          {initiatives.length > 0 ? initiatives.slice(0, 5).map((initiative) => (
            <InitiativeSection
              key={initiative.id}
              initiative={initiative}
              kpis={initiativeKpis[initiative.id] || []}
              onViewDetails={handleInitiativeViewDetails}
              onKPIViewDetails={handleKPIViewDetails}
              canApprove={canApprove}
              cardWidth={horizontalCardWidth}
              onApprove={handleApproveInitiative}
              onReject={handleRejectInitiative}
            />
          )) : (
            <View style={styles.emptySection}>
              <Text style={[styles.emptySectionText, { color: themeColors.textMuted }]}>{t('dashboard.noInitiatives')}</Text>
            </View>
          )}
        </View>
          {/* Decision Maker Cards - متخذ القرار */}
        <View style={[styles.decisionMakerRow, isSmallScreen && styles.decisionMakerRowSmall]}>
          <TouchableOpacity
            style={[styles.decisionMakerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => navigation.navigate('Employees', { organizationId, organizationName: translateOrg(organization.name) })}
            activeOpacity={0.7}
          >
            <View style={styles.decisionMakerCardHeader}>
              <Text style={[styles.decisionMakerCardTitle, { color: themeColors.textPrimary }]}>{t('organizations.decisionMaker.humanCapital')}</Text>
              <ContactRoundIcon size={24} color={themeColors.iconDefault} />
            </View>
            <View style={styles.decisionMakerChartContainer}>
              <CircularGauge
                percentage={organization?.human_capital_commitment ?? 0}
                size={133}
                strokeWidth={10}
                color={commitmentColor(organization?.human_capital_commitment)}
                backgroundColor={themeColors.circleTrack}
                textColor={themeColors.gaugeText}
              />
            </View>
            <TouchableOpacity
              style={styles.decisionMakerLink}
              onPress={() => navigation.navigate('Employees', { organizationId, organizationName: translateOrg(organization.name) })}
            >
              <ChevronLeftIcon size={16} color={themeColors.textPrimary} />
              <Text style={[styles.decisionMakerLinkText, { color: themeColors.textPrimary }]}>{t('common.viewDetails')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.decisionMakerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => navigation.navigate('UnitGoals', { organizationId, organizationName: translateOrg(organization.name) })}
            activeOpacity={0.7}
          >
            <View style={styles.decisionMakerCardHeader}>
              <Text style={[styles.decisionMakerCardTitle, { color: themeColors.textPrimary }]}>{t('organizations.decisionMaker.unitGoals')}</Text>
              <BuildingIcon size={24} color={themeColors.iconDefault} />
            </View>
            <View style={styles.decisionMakerChartContainer}>
              <CircularGauge
                percentage={organization?.units_commitment ?? 0}
                size={133}
                strokeWidth={10}
                color={commitmentColor(organization?.units_commitment)}
                backgroundColor={themeColors.circleTrack}
                textColor={themeColors.gaugeText}
              />
            </View>
            <TouchableOpacity
              style={styles.decisionMakerLink}
              onPress={() => navigation.navigate('UnitGoals', { organizationId, organizationName: translateOrg(organization.name) })}
            >
              <ChevronLeftIcon size={16} color={themeColors.textPrimary} />
              <Text style={[styles.decisionMakerLinkText, { color: themeColors.textPrimary }]}>{t('common.viewDetails')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.decisionMakerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => navigation.navigate('Services', { organizationId, organizationName: translateOrg(organization.name) })}
            activeOpacity={0.7}
          >
            <View style={styles.decisionMakerCardHeader}>
              <Text style={[styles.decisionMakerCardTitle, { color: themeColors.textPrimary }]}>{t('organizations.decisionMaker.services')}</Text>
              <HandServicesIcon size={24} color={themeColors.iconDefault} />
            </View>
            <View style={styles.decisionMakerChartContainer}>
              <CircularGauge
                percentage={organization?.services_commitment ?? 0}
                size={133}
                strokeWidth={10}
                color={commitmentColor(organization?.services_commitment)}
                backgroundColor={themeColors.circleTrack}
                textColor={themeColors.gaugeText}
              />
            </View>
            <TouchableOpacity
              style={styles.decisionMakerLink}
              onPress={() => navigation.navigate('Services', { organizationId, organizationName: translateOrg(organization.name) })}
            >
              <ChevronLeftIcon size={16} color={themeColors.textPrimary} />
              <Text style={[styles.decisionMakerLinkText, { color: themeColors.textPrimary }]}>{t('common.viewDetails')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.decisionMakerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
            onPress={() => navigation.navigate('Processes', { organizationId, organizationName: translateOrg(organization.name) })}
            activeOpacity={0.7}
          >
            <View style={styles.decisionMakerCardHeader}>
              <Text style={[styles.decisionMakerCardTitle, { color: themeColors.textPrimary }]}>{t('organizations.decisionMaker.processes')}</Text>
              <FolderCogIcon size={24} color={themeColors.iconDefault} />
            </View>
            <View style={styles.decisionMakerChartContainer}>
              <CircularGauge
                percentage={organization?.processes_commitment ?? 0}
                size={133}
                strokeWidth={10}
                color={commitmentColor(organization?.processes_commitment)}
                backgroundColor={themeColors.circleTrack}
                textColor={themeColors.gaugeText}
              />
            </View>
            <TouchableOpacity
              style={styles.decisionMakerLink}
              onPress={() => navigation.navigate('Processes', { organizationId, organizationName: translateOrg(organization.name) })}
            >
              <ChevronLeftIcon size={16} color={themeColors.textPrimary} />
              <Text style={[styles.decisionMakerLinkText, { color: themeColors.textPrimary }]}>{t('common.viewDetails')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </View>

        {/* Org Structure Image (الهيكل التنظيمي) */}
        {organization.org_structure_file && (
          <View style={[styles.sectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.textPrimary, marginBottom: tokens.spacing.absolute["3"] }]}>
              {t('orgChart.orgStructure')}
            </Text>
            <View style={{ backgroundColor: themeColors.backgroundAlt, borderRadius: tokens.radii.absolute["radius-8"], padding: tokens.spacing.absolute["2"] }}>
              <Image
                source={{ uri: (() => {
                  const path = organization.org_structure_file;
                  if (!path) return '';
                  if (path.startsWith('http')) return path;
                  const origin = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
                  return `${origin}${path}`;
                })() }}
                style={{ width: '100%', aspectRatio: 16 / 9 }}
                resizeMode="contain"
              />
            </View>
          </View>
        )}

        {/* Organizational Units Chart (مخطط الوحدات التنظيمية) */}
        <OrganizationalChart
          organizationId={organizationId}
          organizationName={translateOrg(organization.name)}
          onNodePress={(node) => {
            navigation.navigate('OrganizationalUnitDetail' as any, { id: node.id });
          }}
          onMoveComplete={fetchData}
        />
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
    paddingBottom: tokens.spacing.absolute["10"],
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["2"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.blue["500"],
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginHorizontal: tokens.spacing.absolute["2"],
  },
  breadcrumbCurrent: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general["Main Color"],
  },

  // Page Title
  pageTitle: {
    fontSize: tokens.typography["heading 3"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },

  // Main Content Layout
  mainContent: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  mainContentSmall: {
    flexDirection: 'column',
  },
  leftColumn: {
    flex: 1,
    gap: tokens.spacing.absolute["4"],
  },
  leftColumnSmall: {
    flex: 0,
  },
  rightColumn: {
    flex: 1.5,
  },
  rightColumnSmall: {
    flex: 0,
  },

  // KPI Gauge Cards
  kpiGaugeCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
  },
  kpiGaugeCardBody: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  kpiGaugeTextSection: {
    flex: 1,
    alignItems: 'flex-end',
  },
  kpiGaugeTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  kpiGaugeDescription: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 18,
  },

  // Legend
  legendItems: {
    gap: tokens.spacing.absolute["2"],
    marginTop: tokens.spacing.absolute["2"],
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  legendRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  legendRowLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  legendRowValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  legendDotIndicator: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },

  // Organization Card
  orgCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
  },
  orgName: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  orgContent: {
    gap: tokens.spacing.absolute["3"],
  },
  orgSectionTitle: {
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  orgSectionText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 22,
  },
  orgStatsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
    marginTop: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["4"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.slate["200"],
  },
  orgStatPill: {
    backgroundColor: tokens.colors.raw.gray["50"],
    borderRadius: tokens.radii.absolute["radius-20"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  orgStatPillLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  orgStatPillValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },

  // Stats Row
  statsRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  statsRowSmall: {
    flexWrap: 'wrap',
  },
  statCard: {
    flex: 1,
    minWidth: 180,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
  },
  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["3"],
  },
  statCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  statCardHovered: {
    transform: [{ scale: 1.02 }],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  statCardValue: {
    fontSize: 32,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
    textAlign: getTextAlign(isRTL),
  },

  // Section Card
  sectionCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["5"],
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionPercentageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },

  // Goal Section
  goalSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
  },
  goalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["2"],
  },
  goalLeftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["5"],
  },
  viewDetailsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255,255,255,0.5)',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
  },
  viewDetailsBtnText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  percentageGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  percentageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },
  goalTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
  },
  goalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  bullet: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
  },

  // Approval Buttons
  approvalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["2"],
  },

  // Horizontal scroll container for KPI cards
  horizontalScrollContainer: {
    flexGrow: 0,
  },
  // RTL wrapper - flips the entire ScrollView horizontally so cards start from right
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  // RTL card - flips each card back to normal orientation
  rtlCard: {
    transform: [{ scaleX: -1 }],
  },
  horizontalCardsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  kpiCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    width: 288, // Fixed width (w-72)
    flexShrink: 0, // Prevent shrinking
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    gap: tokens.spacing.absolute["3"],
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  kpiValueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  kpiValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  kpiProgressContainer: {
    flex: 1,
  },
  kpiProgressTrack: {
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'hidden',
  },
  kpiProgressFill: {
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'absolute',
    end: 0,
    top: 0,
  },
  kpiDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  kpiDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  emptyKpis: {
    padding: tokens.spacing.absolute["4"],
    alignItems: 'center',
  },
  emptyKpisText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
  },
  emptySection: {
    padding: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
  emptySectionText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },

  // Decision Maker Cards
  decisionMakerRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  decisionMakerRowSmall: {
    flexWrap: 'wrap',
  },
  decisionMakerCard: {
    flex: 1,
    minWidth: 180,
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  decisionMakerCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    width: '100%',
  },
  decisionMakerCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  decisionMakerChartContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  decisionMakerLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    width: '100%',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
  },
  decisionMakerLinkText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
});

// ============================================================
// Org Chart Styles
// ============================================================


const styles = getStyles(true);
